import type { TourDefinition, TourStep } from './types';

export const TOUR_ID_PATTERN = /^[a-z0-9]+([.-][a-z0-9]+)*$/;

/** Seconds a step is assumed to take; the help card shows steps × this, rounded up to minutes. */
export const SECONDS_PER_STEP = 20;

/** Identity helper so a definition file gets the type checked without annotations. */
export function defineTour(definition: TourDefinition): TourDefinition {
  return definition;
}

export interface TourRegistry {
  list: readonly TourDefinition[];
  get: (id: string) => TourDefinition | undefined;
}

export function createTourRegistry(definitions: readonly TourDefinition[]): TourRegistry {
  const byId = new Map<string, TourDefinition>();
  for (const definition of definitions) {
    if (byId.has(definition.id)) throw new Error(`Duplicate tour id "${definition.id}"`);
    byId.set(definition.id, definition);
  }
  return { list: definitions, get: (id) => byId.get(id) };
}

/** i18n keys (tours namespace) for one step's title and body. */
export function stepKeys(tourId: string, step: Pick<TourStep, 'id'>, copy = 'body') {
  return {
    title: `${tourId}.steps.${step.id}.title`,
    body: `${tourId}.steps.${step.id}.${copy}`,
  };
}

export const tourTitleKey = (tourId: string) => `${tourId}.title`;
export const tourSummaryKey = (tourId: string) => `${tourId}.summary`;
export const tourFinishKey = (tourId: string) => `${tourId}.finish`;

/** Every key a definition reads; the registry tests check each exists and none is orphaned. */
export function tourKeys(definition: TourDefinition): string[] {
  const keys = [
    tourTitleKey(definition.id),
    tourSummaryKey(definition.id),
    tourFinishKey(definition.id),
  ];
  for (const step of definition.steps) {
    const copies = new Set(['body', ...(step.variants ?? []).map((v) => v.copy ?? 'body')]);
    keys.push(stepKeys(definition.id, step).title);
    for (const copy of copies) keys.push(stepKeys(definition.id, step, copy).body);
  }
  if (definition.resolve) keys.push(definition.resolve.thing);
  return keys;
}

/** Every `data-tour` value a definition points at. */
export function tourTargets(definition: TourDefinition): string[] {
  return definition.steps.flatMap((step) => [
    ...(step.target ? [step.target] : []),
    ...(step.variants ?? []).map((v) => v.target),
    ...(step.advanceOn && 'appears' in step.advanceOn ? [step.advanceOn.appears] : []),
  ]);
}

export function minutesFor(stepCount: number): number {
  return Math.max(1, Math.ceil((stepCount * SECONDS_PER_STEP) / 60));
}
